import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { restaurantApi, favoriteApi, sessionApi } from '../services/api';
import { CatalogRestaurant, City } from '../types';
import { pluralize } from '../utils/pluralize';
import { saveSession } from '../utils/sessionHistory';
import { useAuth } from '../context/AuthContext';
import { useSession } from '../context/SessionContext';
import UnifiedFilters, { FilterState } from '../components/UnifiedFilters';
import { PhoneIcon, LocationPinIcon, GlobeIcon, CategoryPlaceholder } from '../components/Icons';
import WorkingHoursBadge from '../components/WorkingHoursBadge';
import Button from '../components/Button';
import ProfileButton from '../components/ProfileButton';
import Select from '../components/Select';

const SORT_OPTIONS = [
  { label: 'По рейтингу', value: 'rating' },
  { label: 'Часто выбирают', value: 'popular' },
  { label: 'Новинки', value: 'new' },
  { label: 'По отзывам', value: 'reviews' },
  { label: 'По названию', value: 'name' },
  { label: 'Чек ↑', value: 'check_asc' },
  { label: 'Чек ↓', value: 'check_desc' },
];


const CatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, requestAuth } = useAuth();
  const { setParticipantId } = useSession();

  // Favorites
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Data
  const [cities, setCities] = useState<City[]>([]);
  const [restaurants, setRestaurants] = useState<CatalogRestaurant[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters — unified state
  const [citySlug, setCitySlug] = useState('tomsk');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterState, setFilterState] = useState<FilterState>({
    rubrics: [],
    cuisineTags: [],
    menuTags: [],
    features: [],
    groupChains: true,
  });
  const [sortBy, setSortBy] = useState('rating');
  // page state managed via pageRef for infinite scroll

  // Geolocation
  const [userLocation, setUserLocation] = useState<{ latitude: number; longitude: number } | null>(null);

  // Derived from filterState for API compatibility
  const selectedRubrics = filterState.rubrics;
  const selectedCuisineTags = filterState.cuisineTags;
  const selectedMenuTags = filterState.menuTags;
  const selectedFeatures = useMemo(() => filterState.features || [], [filterState.features]);
  const minRating = filterState.minRating;
  const groupChains = filterState.groupChains ?? true;
  const geoActive = filterState.geoActive ?? false;

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Filters panel visibility (mobile)
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll-to-top button
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close filters on click outside (desktop only)
  useEffect(() => {
    if (!showFilters || isMobile) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setShowFilters(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters, isMobile]);

  // Expanded card
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Debounce search
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load cities on mount
  useEffect(() => {
    restaurantApi.getCities().then((data) => {
      setCities(data.cities);
    }).catch(() => {});
  }, []);

  // Load favorites when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      favoriteApi.list().then((data) => {
        setFavoriteIds(new Set(data.restaurantIds));
      }).catch(() => {});
    } else {
      setFavoriteIds(new Set());
      setShowFavoritesOnly(false);
    }
  }, [isAuthenticated]);

  // Infinite scroll
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef(1);

  // Load restaurants (page 1 = reset, page > 1 = append)
  const loadRestaurants = useCallback(async (pageNum: number) => {
    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const data = await restaurantApi.getCatalog({
        citySlug,
        search: debouncedSearch || undefined,
        rubrics: selectedRubrics.length > 0 ? selectedRubrics : undefined,
        cuisineTags: selectedCuisineTags.length > 0 ? selectedCuisineTags : undefined,
        menuTags: selectedMenuTags.length > 0 ? selectedMenuTags : undefined,
        features: selectedFeatures.length > 0 ? selectedFeatures : undefined,
        minRating,
        sortBy,
        groupChains,
        page: pageNum,
        limit: 50,
      });
      if (pageNum === 1) {
        setRestaurants(data.restaurants);
      } else {
        setRestaurants((prev) => [...prev, ...data.restaurants]);
      }
      setTotalCount(data.totalCount);
      setHasMore(pageNum * 50 < data.totalCount);
    } catch {
      // ignore
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [citySlug, debouncedSearch, selectedRubrics, selectedCuisineTags, selectedMenuTags, selectedFeatures, minRating, sortBy, groupChains]);

  // Reset to page 1 when filters change
  useEffect(() => {
    pageRef.current = 1;
    setHasMore(true);
    loadRestaurants(1);
  }, [loadRestaurants]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          pageRef.current += 1;
          loadRestaurants(pageRef.current);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, loadRestaurants]);

  // Debounced search
  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(value);
          }, 400);
  };

  const handleGeolocation = () => {
    if (geoActive) {
      setFilterState(prev => ({ ...prev, geoActive: false }));
      setUserLocation(null);
      return;
    }
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        setFilterState(prev => ({ ...prev, geoActive: true }));
      },
      () => {},
    );
  };

  const handleFilterChange = (newFilters: FilterState) => {
    setFilterState(newFilters);
      };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const allIds = new Set(selectedIds);
    restaurants.forEach((r) => allIds.add(r.id));
    setSelectedIds(allIds);
  };

  const deselectAll = () => {
    const remaining = new Set(selectedIds);
    restaurants.forEach((r) => remaining.delete(r.id));
    setSelectedIds(remaining);
  };

  const handleCreateSession = () => {
    const ids = Array.from(selectedIds);
    navigate('/create', { state: { restaurantIds: ids, citySlug } });
  };

  const handleNewSession = () => {
    navigate('/create', { state: { citySlug } });
  };

  const [creatingFromFavorites, setCreatingFromFavorites] = useState(false);

  const handleCreateFromFavorites = async () => {
    if (creatingFromFavorites || !user?.name) return;
    setCreatingFromFavorites(true);
    try {
      const data = await sessionApi.create({
        citySlug,
        creatorNickname: user.name,
        restaurantIds: Array.from(favoriteIds),
      });
      setParticipantId(data.participantId);
      localStorage.setItem(`session_${data.sessionId}_pid`, data.participantId);
      localStorage.setItem(`session_${data.sessionId}_link`, data.shareLink);
      saveSession(data.sessionId);
      navigate(`/session/${data.sessionId}/lobby`);
    } catch {
      setCreatingFromFavorites(false);
    }
  };

  const toggleFavorite = async (restaurantId: string) => {
    if (!isAuthenticated) {
      const result = await requestAuth();
      if (!result) return;
      // Reload favorites after login
      try {
        const data = await favoriteApi.list();
        setFavoriteIds(new Set(data.restaurantIds));
      } catch {}
      return;
    }

    const isFav = favoriteIds.has(restaurantId);
    // Optimistic update
    setFavoriteIds((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(restaurantId);
      else next.add(restaurantId);
      return next;
    });

    try {
      if (isFav) {
        await favoriteApi.remove(restaurantId);
      } else {
        await favoriteApi.add(restaurantId);
      }
    } catch {
      // Revert on error
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(restaurantId);
        else next.delete(restaurantId);
        return next;
      });
    }
  };

  // Distance calculation (haversine, km)
  const getDistance = (lat: number, lon: number): string | null => {
    if (!userLocation) return null;
    const R = 6371;
    const dLat = ((lat - userLocation.latitude) * Math.PI) / 180;
    const dLon = ((lon - userLocation.longitude) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((userLocation.latitude * Math.PI) / 180) *
        Math.cos((lat * Math.PI) / 180) *
        Math.sin(dLon / 2) ** 2;
    const d = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    if (d < 1) return `${Math.round(d * 1000)} м`;
    return `${d.toFixed(1)} км`;
  };

  const allOnPageSelected = restaurants.length > 0 && restaurants.every((r) => selectedIds.has(r.id));
  const activeFilterCount = selectedRubrics.length + selectedCuisineTags.length + selectedMenuTags.length + selectedFeatures.length + (minRating ? 1 : 0);

  const resetFilters = () => {
    setFilterState({ rubrics: [], cuisineTags: [], menuTags: [], features: [], groupChains: true });
  };

  // Check if restaurant is new (added in last 7 days)
  const isNew = (createdAt?: string) => {
    if (!createdAt) return false;
    const diff = Date.now() - new Date(createdAt).getTime();
    return diff < 7 * 24 * 60 * 60 * 1000;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-md mx-auto px-4">
          {/* Row 1: Logo + City + Actions */}
          <div className="flex items-center gap-3 py-3">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <img src="/logo.svg" alt="SwipeEat" className="w-7 h-7 flex-shrink-0" />
              <span className="text-xl font-display font-bold text-gray-900">Swipe<span className="text-orange-500">Eat</span></span>
            </button>
            {/* City selector — subtle, no border, no chevron */}
            <Select
              value={citySlug}
              options={cities.length > 0 ? cities.map(c => ({ label: c.name, value: c.slug })) : [{ label: 'Томск', value: 'tomsk' }]}
              onChange={(v) => { setCitySlug(v); /* page resets via useEffect */ }}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              hideChevron
              icon={
                <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" /><circle cx="12" cy="9" r="2.5" />
                </svg>
              }
            />
            <div className="flex-1" />
            <ProfileButton />
          </div>

          {/* Row 2: Search bar */}
          <div className="relative flex items-center bg-gray-50 rounded-btn border border-gray-200 focus-within:border-orange-400 focus-within:ring-2 focus-within:ring-orange-100 transition-all mb-2.5">
            {/* Search icon (SVG) */}
            <svg className="ml-3 w-4 h-4 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Название, кухня, блюдо..."
              className="flex-1 bg-transparent px-2.5 py-2.5 text-sm outline-none placeholder:text-gray-400"
            />
            {/* Clear button — only show if search is not empty */}
            {search && (
              <button
                onClick={() => handleSearchChange('')}
                className="mr-3 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Clear search"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          {/* Row 3: Controls + filters — wrapped for click-outside */}
          <div ref={filtersRef}>
            {/* Controls bar — responsive scrollable on mobile */}
            <div className="flex items-center gap-2 pb-3 overflow-x-auto md:overflow-visible">
              {/* Filters toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="6" x2="20" y2="6" /><line x1="8" y1="12" x2="20" y2="12" /><line x1="12" y1="18" x2="20" y2="18" />
                </svg>
                Фильтры
                {activeFilterCount > 0 && (
                  <span className="text-[10px] font-bold text-white/80 leading-none">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Sort dropdown — custom, no chevron */}
              <Select
                value={sortBy}
                options={SORT_OPTIONS.map(s => ({ label: s.label, value: s.value }))}
                onChange={(v) => { setSortBy(v); /* page resets via useEffect */ }}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors flex-shrink-0 ${
                  sortBy !== 'rating'
                    ? 'bg-orange-100 text-orange-600 hover:bg-orange-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                hideChevron
                icon={
                  <svg className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 6h18M6 12h12M9 18h6" />
                  </svg>
                }
              />

              {/* Favorites filter */}
              <button
                onClick={() => {
                  if (!isAuthenticated) {
                    requestAuth().then((result) => {
                      if (result) {
                        favoriteApi.list().then((data) => {
                          setFavoriteIds(new Set(data.restaurantIds));
                          setShowFavoritesOnly(true);
                        }).catch(() => {});
                      }
                    });
                    return;
                  }
                  setShowFavoritesOnly(!showFavoritesOnly);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  showFavoritesOnly
                    ? 'bg-orange-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={showFavoritesOnly ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                Избранное
                {favoriteIds.size > 0 && isAuthenticated && (
                  <span className={`leading-none ${showFavoritesOnly ? 'text-white/70' : 'text-gray-400'}`}>
                    · {favoriteIds.size}
                  </span>
                )}
              </button>

              {/* Geolocation */}
              <button
                onClick={handleGeolocation}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                  geoActive
                    ? 'bg-green-500 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={geoActive ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                  <circle cx="12" cy="9" r="2.5" />
                </svg>
                {geoActive ? 'Рядом' : 'Локация'}
              </button>

              {/* Spacer — only on desktop */}
              <div className="hidden md:flex-1" />

              {/* Invisible spacer for scroll padding on mobile */}
              <div className="w-2 md:hidden flex-shrink-0" />
            </div>

            {/* Filters panel — modal on mobile, inline on desktop */}
            {isMobile ? (
              // Mobile modal — rendered via Portal to escape backdrop-filter stacking context
              showFilters && createPortal(
                <div className="fixed inset-0 z-50 bg-black/40 transition-opacity duration-200" onClick={() => setShowFilters(false)}>
                  <div
                    className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl flex flex-col animate-slide-up"
                    style={{ maxHeight: '80vh' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Header — fixed, never scrolls */}
                    <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b rounded-t-3xl bg-white">
                      <div className="flex items-center gap-2">
                        <h2 className="font-semibold text-gray-900">Фильтры</h2>
                        {activeFilterCount > 0 && (
                          <span className="text-sm font-bold text-orange-500 leading-none">{activeFilterCount}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {activeFilterCount > 0 && (
                          <button
                            onClick={resetFilters}
                            className="text-xs text-orange-500 font-medium hover:text-orange-600 transition-colors"
                          >
                            Сбросить
                          </button>
                        )}
                        <button
                          onClick={() => setShowFilters(false)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Scrollable filters content — takes all remaining space */}
                    <div className="flex-1 overflow-y-auto p-4">
                      <UnifiedFilters
                        filters={filterState}
                        onChange={handleFilterChange}
                        showChains
                        showRating
                        citySlug={citySlug}
                      />
                    </div>

                    {/* Footer — fixed, never scrolls */}
                    <div className="flex-shrink-0 bg-white border-t p-4">
                      <Button fullWidth onClick={() => setShowFilters(false)}>
                        Готово
                      </Button>
                    </div>
                  </div>
                </div>,
                document.body
              )
            ) : (
              // Desktop inline filters — smooth grid animation
              <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${showFilters ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                <div className="overflow-hidden">
                  <div className="pb-3 border-t border-gray-100 pt-3">
                    {activeFilterCount > 0 && (
                      <div className="flex justify-end mb-2">
                        <button
                          onClick={resetFilters}
                          className="text-xs text-orange-500 font-medium hover:text-orange-600 transition-colors"
                        >
                          Сбросить все
                        </button>
                      </div>
                    )}
                    <UnifiedFilters
                      filters={filterState}
                      onChange={handleFilterChange}
                      showChains
                      showRating
                      citySlug={citySlug}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New session CTA — scrolls with content, not in sticky */}
      <div className="max-w-md mx-auto px-4 pt-3">
        <Button fullWidth size="sm" onClick={handleNewSession}>
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Новая сессия — мы подберём заведения
        </Button>
      </div>

      {/* Results info + bulk actions */}
      <div className="max-w-md mx-auto px-4 py-2">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            {totalCount} заведений
            {selectedIds.size > 0 && (
              <span className="ml-2 text-orange-600 font-medium">
                ({selectedIds.size} выбрано)
              </span>
            )}
          </span>
          <button onClick={allOnPageSelected ? deselectAll : selectAll} className="text-orange-600 text-xs flex-shrink-0">
            {allOnPageSelected ? 'Снять все' : 'Выбрать все'}
          </button>
        </div>
      </div>

      {/* Restaurant list */}
      <div className="max-w-md mx-auto px-4 pb-24">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-400">Загрузка...</div>
          </div>
        ) : restaurants.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            Ничего не найдено
          </div>
        ) : (
          <div className="space-y-2">
            {(showFavoritesOnly ? restaurants.filter((r) => favoriteIds.has(r.id)) : restaurants).map((r) => {
              const isSelected = selectedIds.has(r.id);
              const isExpanded = expandedId === r.id;
              const distance = getDistance(r.latitude, r.longitude);

              return (
                <div
                  key={r.id}
                  className={`bg-white rounded-xl border-2 transition-colors overflow-hidden ${
                    isSelected ? 'border-orange-500' : 'border-transparent'
                  }`}
                >
                  {/* Main row — items-center for checkbox vertical alignment */}
                  <div className="flex items-center p-3 gap-3">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleSelect(r.id)}
                      className={`w-6 h-6 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                        isSelected
                          ? 'bg-orange-500 border-orange-500 text-white'
                          : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <span className="text-xs">✓</span>}
                    </button>

                    {/* Photo — category-specific icon, no gradient */}
                    <div
                      className="w-14 h-14 rounded-lg flex-shrink-0 overflow-hidden cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : r.id)}
                    >
                      {r.photoUrl ? (
                        <img src={r.photoUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <CategoryPlaceholder
                          rubrics={r.rubrics}
                          cuisine={r.cuisine}
                          className="w-full h-full rounded-lg"
                          iconClassName="w-6 h-6"
                        />
                      )}
                    </div>

                    {/* Info */}
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : r.id)}
                    >
                      <h3 className="font-semibold text-gray-900 text-sm line-clamp-2 font-body">{r.name}</h3>
                      <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
                        <p className="text-xs text-gray-500 truncate min-w-0 flex-1">{r.cuisine}</p>
                        {r.chainName && r.branchCount && r.branchCount > 1 && (
                          <span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">
                            · {pluralize(r.branchCount, 'филиал', 'филиала', 'филиалов')}
                          </span>
                        )}
                      </div>
                      {distance && (
                        <p className="text-[11px] text-green-600 mt-0.5">{distance}</p>
                      )}
                      <WorkingHoursBadge workingHours={r.workingHours as any} className="mt-0.5" />
                    </div>

                    {/* Right: ★ rating (reviews) on top, 🤍 favorite below, NEW badge */}
                    <div className="flex-shrink-0 flex flex-col items-end gap-0.5">
                      {r.rating && (
                        <div className="flex items-center gap-0.5 text-sm">
                          <span className="text-yellow-500">★</span>
                          <span className="font-semibold">{r.rating}</span>
                          {r.reviewCount && (
                            <span className="text-xs text-gray-400">({r.reviewCount})</span>
                          )}
                        </div>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(r.id); }}
                        className="p-0.5 transition-colors"
                      >
                        <svg className={`w-4 h-4 ${favoriteIds.has(r.id) ? 'text-red-500' : 'text-gray-300 hover:text-red-300'}`} viewBox="0 0 24 24" fill={favoriteIds.has(r.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                        </svg>
                      </button>
                      {isNew(r.createdAt) && (
                        <span className="text-[10px] font-bold text-orange-500 tracking-wide leading-none mt-1">NEW</span>
                      )}
                    </div>
                  </div>

                  {/* Expanded details — animated with CSS grid trick */}
                  <div className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                    <div className="overflow-hidden">
                      <div className="px-3 pb-3 border-t border-gray-100">
                        <div className="pt-2 space-y-1.5">
                          {/* Address + Чек in same row — fills the top-right empty space */}
                          <div className="flex items-start justify-between gap-3">
                            <a
                              href={`https://2gis.ru/${citySlug}/firm/${r.externalId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-gray-500 flex items-center gap-1 hover:text-green-600 transition-colors"
                            >
                              <LocationPinIcon className="w-3 h-3 flex-shrink-0 text-gray-400" />
                              {r.address} <span className="text-green-600">↗</span>
                            </a>
                            {r.averageCheck && (
                              <span className="text-xs text-gray-500 flex-shrink-0">{r.averageCheck}</span>
                            )}
                          </div>
                          {/* Working hours only here, removed from collapsed duplicate */}
                          {r.cuisineTags && (
                            <p className="text-xs text-orange-600">{r.cuisineTags}</p>
                          )}
                          {r.menuTags && (
                            <div className="flex flex-wrap gap-1">
                              {r.menuTags.split(', ').map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded-full text-[10px]">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {r.features && (() => {
                            const HIGHLIGHT = ['Доставка', 'Бизнес-ланч', 'Wi-Fi', 'Детское меню', 'Завтраки', 'Можно с собакой'];
                            const present = HIGHLIGHT.filter(f => r.features!.includes(f));
                            return present.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {present.map(f => (
                                  <span key={f} className="px-2 py-0.5 bg-green-50 text-green-700 rounded-full text-[10px] font-medium">
                                    {f}
                                  </span>
                                ))}
                              </div>
                            ) : null;
                          })()}
                          {r.phone && (
                            <a href={`tel:${r.phone}`} className="text-xs text-gray-500 flex items-center gap-1 hover:text-green-600 transition-colors">
                              <PhoneIcon className="w-3 h-3 flex-shrink-0 text-gray-400" />
                              {r.phone}
                            </a>
                          )}
                          {r.website && (
                            <a href={r.website} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 flex items-center gap-1 hover:text-green-600 transition-colors truncate">
                              <GlobeIcon className="w-3 h-3 flex-shrink-0 text-gray-400" />
                              <span className="truncate">{r.website.replace(/^https?:\/\/(www\.)?/, '')}</span>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Infinite scroll sentinel */}
        {hasMore && restaurants.length > 0 && (
          <div ref={sentinelRef} className="flex items-center justify-center py-6">
            {loadingMore && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="w-4 h-4 border-2 border-orange-300 border-t-transparent rounded-full animate-spin" />
                Загружаем ещё...
              </div>
            )}
          </div>
        )}
        {!hasMore && restaurants.length > 50 && (
          <p className="text-center text-xs text-gray-400 py-4">Все заведения загружены</p>
        )}
      </div>

      {/* Bottom action bar — selected or favorites */}
      {(selectedIds.size > 0 || (showFavoritesOnly && favoriteIds.size > 0)) && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t shadow-lg">
          <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
            <div className="text-sm">
              <span className="font-semibold text-orange-600">
                {showFavoritesOnly && selectedIds.size === 0 ? favoriteIds.size : selectedIds.size}
              </span>
              <span className="text-gray-500 ml-1">
                {showFavoritesOnly && selectedIds.size === 0
                  ? 'в избранном'
                  : selectedIds.size === 1 ? 'заведение' : selectedIds.size < 5 ? 'заведения' : 'заведений'
                }
              </span>
            </div>
            <Button
              disabled={creatingFromFavorites}
              onClick={showFavoritesOnly && selectedIds.size === 0 ? handleCreateFromFavorites : handleCreateSession}
            >
              {creatingFromFavorites ? 'Создаём...' : 'Создать сессию'}
            </Button>
          </div>
        </div>
      )}

      {/* Scroll-to-top button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Наверх"
        className={`fixed bottom-6 right-4 z-50 w-10 h-10 rounded-full bg-orange-500 text-white shadow-lg flex items-center justify-center transition-all duration-200 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 block">
          <polyline points="18 15 12 9 6 15" />
        </svg>
      </button>
    </div>
  );
};

export default CatalogPage;
